
import React, { useState } from 'react';
import { Icons } from '../ui/Icons';
import { ReportPermissionLevel, StaffMember, AttendanceRecord } from '../../types';
import { formatDateYYYYMMDD, getWeekStartDate } from '../../utils/dateUtils';

interface PayrollReportProps {
    staffList: StaffMember[];
    attendanceRecords: AttendanceRecord[];
    onInitiatePayrollReport: (startDate: string, endDate: string, employeeId?: string) => void;
    permissionLevel?: ReportPermissionLevel;
    onLevelChange?: (level: 'form' | 'preview') => void;
}

const PayrollReport: React.FC<PayrollReportProps> = ({
    staffList,
    attendanceRecords,
    onInitiatePayrollReport,
    permissionLevel = 'view',
    onLevelChange
}) => {
    const [payrollStart, setPayrollStart] = useState<string>(formatDateYYYYMMDD(getWeekStartDate(new Date(), 1)));
    const [payrollEnd, setPayrollEnd] = useState<string>(() => {
        const d = getWeekStartDate(new Date(), 1);
        d.setDate(d.getDate() + 13); // Default 2 weeks
        return formatDateYYYYMMDD(d);
    });
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('ALL');

    const handleGeneratePayroll = () => {
        onInitiatePayrollReport(payrollStart, payrollEnd, selectedEmployeeId === 'ALL' ? undefined : selectedEmployeeId);
    };

    return (
        <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800 space-y-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-emerald-600">
                        <Icons.FileText className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Payroll Summary</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Select period for hours calculation</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Start Date</label>
                        <input 
                            type="date" 
                            value={payrollStart}
                            onChange={(e) => setPayrollStart(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">End Date</label>
                        <input 
                            type="date" 
                            value={payrollEnd}
                            onChange={(e) => setPayrollEnd(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Employee (Optional)</label>
                    <select 
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                    >
                        <option value="ALL">All Staff Members</option>
                        {staffList.filter(s => s.status !== 'deleted').sort((a, b) => a.name.localeCompare(b.name)).map(staff => (
                            <option key={staff.id} value={staff.id}>{staff.name}</option>
                        ))}
                    </select>
                </div>

                <div className="pt-4">
                    <button 
                        onClick={handleGeneratePayroll}
                        disabled={permissionLevel === 'none'}
                        className="w-full bg-gray-900 dark:bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-gray-200 dark:shadow-none hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-3 group"
                    >
                        <Icons.Printer className="w-5 h-5" />
                        Generate Payroll Report
                        <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <p className="text-center text-[10px] text-gray-400 font-bold uppercase mt-4">This will generate a printable document with calculated hours.</p>
                </div>
            </div>
        </div>
    );
};

export default PayrollReport;
